import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { InputAdornment, Tab, TableBody } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import qs from 'qs';
import dayjs from 'dayjs';

import { FormInputText } from '../../form-components/FormInputText';
import { FormInputDate } from '../../form-components/FormInputDate';
import CommonPagination from '../../form-components/CommonPagination';
import CommonSelect from '../../form-components/CommonSelect';
import { CommonExcelButton } from '../../form-components/CommonExcelButton';
import { errorMessages, exportStyledExcel } from '../../utils/methods';
import ConfirmModal from '../common/modal/ConfirmModal';
import { POINT_TYPE_LABELS, getPointChannelLabel, getPointSourceLabel } from '../../constants/point';

import {
  BodyCell,
  BodyRow,
  DateCellText,
  DateInputWrapper,
  DateSeparator,
  EmptyCell,
  HeaderCell,
  HistorySectionContainer,
  HistoryStyledTabs,
  HistoryToolbarRow,
  BalanceText,
  CancelDetailBox,
  CancelDetailNote,
  CancelDetailRow,
  CancelWarning,
  RowActionButton,
  RowActions,
  MemberNameLink,
  PointValueText,
  SearchIconButton,
  SearchInputWrapper,
  StatusChip,
  StyledTable,
  StyledTableContainer,
  StyledTableHead,
  ToolbarControls,
} from './PointStyled';

// 내역 종류 탭 (전체/적립/차감)
const TAB_OPTIONS = [
  { value: 'ALL', label: '전체 내역' },
  { value: 'EARN', label: '적립 내역' },
  { value: 'DEDUCT', label: '차감 내역' },
  // 유효기간이 있고 아직 잔여가 남은 적립만, 만료 빠른 순
  { value: 'EXPIRING', label: '소멸 예정' },
];

// 페이지 개수 옵션
const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10개씩' },
  { value: 30, label: '30개씩' },
  { value: 50, label: '50개씩' },
];

const DEFAULT_LIMIT = 10;
// 엑셀 다운로드는 현재 필터 조건 전체를 한 번에 조회 (기존 회원 목록 엑셀 컨벤션과 동일 상한)
const EXCEL_FETCH_LIMIT = 10000;

// 포인트 셀 하단에 표기할 '결과 잔액'(balanceAfter) 포맷 — BE가 응답으로 제공.
const formatBalance = row => (row?.balanceAfter == null ? '-' : `${Number(row.balanceAfter).toLocaleString()} P`);

// 적용 일시를 피그마처럼 "2026-04-14(수)" + "11:28:46" 2줄로 분리
const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const formatRegDate = value => {
  if (!value) return { date: '-', time: '' };
  const [datePart, timePart = ''] = String(value).split(' ');
  const parsed = dayjs(datePart);
  const dayName = parsed.isValid() ? `(${DAY_NAMES[parsed.day()]})` : '';
  return { date: `${datePart}${dayName}`, time: timePart };
};

// 유효기간 컬럼 표기 — 적립: 만료일 또는 무기한 / 차감: -
const formatExpire = row => {
  if (row.type !== 'EARN') return '-';
  return row.expireDate || '무기한';
};

// 컬럼 폭은 피그마 테이블 스펙(160/160/320/120/200/93/120/나머지) 기준
const TABLE_COLUMNS = [
  { key: 'regDate', label: '적용 일시', width: 160 },
  { key: 'memberName', label: '회원명', width: 160 },
  { key: 'productName', label: '구입한 상품명', width: 240 },
  { key: 'type', label: '상태', width: 120 },
  { key: 'point', label: '포인트', width: 200 },
  { key: 'expireDate', label: '유효기간', width: 120 },
  { key: 'staffName', label: '담당자', width: 93 },
  { key: 'channel', label: '사용처', width: 120 },
  { key: 'description', label: '내용', width: 220 },
  { key: 'action', label: '변경', width: 150 },
];

/**
 * 포인트 내역 섹션 (회원상세 > 포인트 탭 / 센터관리 > 포인트 관리 공용)
 *
 * - 데이터를 props로 받지 않고, 호출부에서 넘겨준 fetchHistories(queryString)로 내부에서 useQuery 수행.
 * - fetchHistories는 branchId/memberId/token 등을 이미 바인딩한 함수를 넘겨받는다.
 *   예) 회원상세: (queryString) => getMemberPoints(memberId, `${queryString}&branchId=${branchId}`, token)
 *       센터관리: (queryString) => getBranchPointHistories(branchId, queryString, token)
 *
 * Props:
 * - fetchHistories(queryString): Promise<{ content, totalElements, ... }>
 * - queryKeyPrefix: React Query queryKey 앞부분 (예: ['memberPoints', memberId, branchId])
 * - showSearch: 이름/연락처 검색 인풋 노출 여부 (기본 false). 기간 필터/엑셀 다운로드/
 *   페이지 사이즈 선택/페이지네이션은 showSearch 값과 무관하게 항상 노출된다.
 * - onMemberClick(row): 회원명 클릭 핸들러 (없으면 클릭 불가 텍스트로 표시)
 * - enabled: useQuery 활성화 조건 (branchId/memberId 준비 여부 등 호출부에서 제어)
 * - excelFilename: 엑셀 파일명 접두어
 */
// 기간 프리셋 옵션 (센터관리 포인트 관리 — showPeriodPreset일 때만 노출)
const PERIOD_PRESET_OPTIONS = [
  { value: 'ALL', label: '전체' },
  { value: 'DAILY', label: '일간' },
  { value: 'WEEKLY', label: '주간' },
  { value: 'MONTHLY', label: '월간' },
  { value: 'CUSTOM', label: '직접 설정' },
];

const PointHistorySection = ({
  fetchHistories,
  queryKeyPrefix = [],
  showSearch = false,
  showPeriodPreset = false,
  onMemberClick,
  enabled = true,
  excelFilename = '포인트_내역',
  // 적립 건 취소 — (row) => Promise. 넘기면 수기·일괄 적립 행에 '지급 취소' 버튼이 뜬다.
  onCancelGrant,
  // 내역 수정 — (row) => void. 넘기면 수정 가능한(수기·일괄, 미취소) 행을 클릭할 수 있다.
  onEditHistory,
  // 취소 후 함께 무효화할 쿼리 키(예: 회원상세 헤더 잔액). queryKeyPrefix 는 항상 무효화된다.
  invalidateKeysAfterChange = [],
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  // 취소 확인 대상 행 (null 이면 확인 모달 닫힘)
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelingId, setCancelingId] = useState(null);

  const runCancelGrant = async () => {
    const row = cancelTarget;
    setCancelTarget(null);
    if (!row || !onCancelGrant) return;
    setCancelingId(row.pointHistoryId);
    try {
      const result = await onCancelGrant(row);
      toast.success(`${Number(result?.reclaimedPoint || 0).toLocaleString()}P ${row.type === 'EARN' ? t('회수했습니다') : t('복구했습니다')}`);
      queryClient.invalidateQueries({ queryKey: queryKeyPrefix });
      invalidateKeysAfterChange.forEach(key => queryClient.invalidateQueries({ queryKey: key }));
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setCancelingId(null);
    }
  };

  // 적립 취소 시 이미 사용된 양 = 적립액 - 남은 포인트 (이 부분은 회수되지 않는다)
  const cancelUsedPoint =
    cancelTarget?.type === 'EARN' ? Math.max(0, Number(cancelTarget.point || 0) - Number(cancelTarget.remainPoint || 0)) : 0;

  const [tab, setTab] = useState('ALL');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [appliedSearch, setAppliedSearch] = useState({ search: '', startDate: null, endDate: null });
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [period, setPeriod] = useState('ALL');

  const { control, getValues, setValue } = useForm({
    defaultValues: { search: '', startDate: null, endDate: null },
  });

  // 현재 필터 상태로 API 쿼리스트링 생성 (엑셀 다운로드 시 page/limit만 덮어써서 재사용)
  const buildQueryString = (overrides = {}) => {
    const params = {
      type: tab,
      page,
      limit,
      ...(appliedSearch.search ? { search: appliedSearch.search } : {}),
      ...(appliedSearch.startDate ? { startDate: dayjs(appliedSearch.startDate).format('YYYY-MM-DD') } : {}),
      ...(appliedSearch.endDate ? { endDate: dayjs(appliedSearch.endDate).format('YYYY-MM-DD') } : {}),
      ...overrides,
    };
    return qs.stringify(params);
  };

  const { data, isLoading } = useQuery({
    queryKey: [...queryKeyPrefix, tab, page, limit, appliedSearch.search, appliedSearch.startDate, appliedSearch.endDate],
    queryFn: () => fetchHistories(buildQueryString()),
    enabled: enabled && !!fetchHistories,
  });

  const rows = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;

  const handleTabChange = (_, value) => {
    setTab(value);
    setPage(1);
  };

  const handleChangePage = (_, value) => setPage(value);

  const handleChangePageSize = e => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const handleSearchSubmit = () => {
    const values = getValues();
    setAppliedSearch({ search: values.search, startDate: values.startDate, endDate: values.endDate });
    setPage(1);
  };

  // 날짜 인풋 직접 변경 시 — 프리셋 표시를 '직접 설정'으로 전환 후 조회
  const handleDateChange = () => {
    setPeriod('CUSTOM');
    handleSearchSubmit();
  };

  // 기간 프리셋 선택 — 일간=오늘, 주간=이번 주, 월간=이번 달, 전체=기간 해제
  const handlePeriodChange = e => {
    const next = e.target.value;
    setPeriod(next);
    if (next === 'CUSTOM') return; // 직접 설정은 날짜 인풋으로 조정

    let startDate = null;
    let endDate = null;
    if (next === 'DAILY') {
      startDate = dayjs();
      endDate = dayjs();
    } else if (next === 'WEEKLY') {
      startDate = dayjs().startOf('week');
      endDate = dayjs().endOf('week');
    } else if (next === 'MONTHLY') {
      startDate = dayjs().startOf('month');
      endDate = dayjs().endOf('month');
    }
    setValue('startDate', startDate);
    setValue('endDate', endDate);
    const values = getValues();
    setAppliedSearch({ search: values.search, startDate, endDate });
    setPage(1);
  };

  const handleExcelDownload = async () => {
    setIsExcelLoading(true);
    try {
      const excelQueryString = buildQueryString({ page: 1, limit: EXCEL_FETCH_LIMIT });
      const result = await fetchHistories(excelQueryString);
      const excelRows = (result?.content ?? []).map(row => ({
        '적용 일시': row.regDate ?? '',
        회원명: row.memberName ?? '',
        '구입한 상품명': row.productName ?? '',
        상태: row.canceled ? '취소' : (POINT_TYPE_LABELS[row.type] ?? row.type ?? ''),
        포인트: `${row.type === 'DEDUCT' ? '-' : '+'}${Number(row.point || 0).toLocaleString()} P`,
        잔액: formatBalance(row),
        담당자: row.staffName ?? '',
        사용처: getPointChannelLabel(row.channel),
        유효기간: formatExpire(row),
        내용: row.description || getPointSourceLabel(row.sourceType, row.type),
      }));

      if (excelRows.length === 0) {
        toast.info(t('다운로드할 내역이 없습니다'));
        return;
      }
      // 상한 초과분은 잘려 나가므로 사용자에게 알림
      if ((result?.totalElements ?? 0) > EXCEL_FETCH_LIMIT) {
        toast.warning(
          t(`내역이 ${EXCEL_FETCH_LIMIT.toLocaleString()}건을 초과하여 최근 ${EXCEL_FETCH_LIMIT.toLocaleString()}건만 다운로드됩니다`),
        );
      }
      await exportStyledExcel(excelRows, { filename: excelFilename, sheetName: '포인트 내역' });
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setIsExcelLoading(false);
    }
  };

  return (
    <HistorySectionContainer>
      {/* 피그마 레이아웃: 탭(좌) + 검색/기간/엑셀/페이지크기(우)를 한 줄에 배치 */}
      <HistoryToolbarRow>
        <HistoryStyledTabs value={tab} onChange={handleTabChange} aria-label="point history tabs">
          {TAB_OPTIONS.map(opt => (
            <Tab key={opt.value} value={opt.value} label={t(opt.label)} />
          ))}
        </HistoryStyledTabs>

        <ToolbarControls>
          {showSearch && (
            <SearchInputWrapper>
              <FormInputText
                control={control}
                name="search"
                placeholder={t('이름 및 연락처로 검색')}
                size="small"
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSearchSubmit();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIconButton aria-label="search" onClick={handleSearchSubmit}>
                        <SearchIcon />
                      </SearchIconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </SearchInputWrapper>
          )}

          <DateInputWrapper>
            <FormInputDate name="startDate" control={control} placeholder={t('시작일')} onChange={handleDateChange} />
          </DateInputWrapper>
          <DateSeparator>~</DateSeparator>
          <DateInputWrapper>
            <FormInputDate name="endDate" control={control} placeholder={t('종료일')} onChange={handleDateChange} />
          </DateInputWrapper>

          {showPeriodPreset && (
            <CommonSelect value={period} onChange={handlePeriodChange} options={PERIOD_PRESET_OPTIONS} width={100} />
          )}

          <CommonExcelButton onClick={handleExcelDownload} disabled={isExcelLoading || isLoading}>
            {t('전체 엑셀 다운로드')}
          </CommonExcelButton>
          <CommonSelect value={limit} onChange={handleChangePageSize} options={PAGE_SIZE_OPTIONS} width={110} />
        </ToolbarControls>
      </HistoryToolbarRow>

      <StyledTableContainer>
        <StyledTable size="small" aria-label="point history table">
          <StyledTableHead>
            <BodyRow>
              {TABLE_COLUMNS.map(col => (
                <HeaderCell key={col.key} width={col.width}>
                  {t(col.label)}
                </HeaderCell>
              ))}
            </BodyRow>
          </StyledTableHead>

          <TableBody>
            {isLoading ? (
              <BodyRow>
                <EmptyCell colSpan={TABLE_COLUMNS.length}>{t('불러오는 중입니다')}</EmptyCell>
              </BodyRow>
            ) : rows.length === 0 ? (
              <BodyRow>
                <EmptyCell colSpan={TABLE_COLUMNS.length}>{t('내역이 없습니다')}</EmptyCell>
              </BodyRow>
            ) : (
              rows.map((row, index) => {
                const regDate = formatRegDate(row.regDate);
                return (
                  <BodyRow key={row.pointHistoryId ?? index}>
                    <BodyCell>
                      <DateCellText>{regDate.date}</DateCellText>
                      {regDate.time && <DateCellText>{regDate.time}</DateCellText>}
                    </BodyCell>
                    <BodyCell>
                      {onMemberClick ? (
                        <MemberNameLink onClick={() => onMemberClick(row)}>{row.memberName ?? '-'}</MemberNameLink>
                      ) : (
                        row.memberName ?? '-'
                      )}
                    </BodyCell>
                    <BodyCell>{row.productName ?? '-'}</BodyCell>
                    <BodyCell>
                      <StatusChip chipType={row.type} muted={row.canceled}>
                        {row.canceled ? t('취소') : (POINT_TYPE_LABELS[row.type] ?? row.type)}
                      </StatusChip>
                    </BodyCell>
                    <BodyCell>
                      {/* 지급 취소된 적립은 금액에 취소선 — 회수돼서 더 이상 유효하지 않다는 뜻 */}
                      <PointValueText chipType={row.type} sx={row.canceled ? { textDecoration: 'line-through', color: '#9AA0A9' } : undefined}>
                        {row.type === 'DEDUCT' ? '-' : '+'}
                        {Number(row.point || 0).toLocaleString()} P
                      </PointValueText>
                      {/* 소멸 예정 탭은 원 적립액보다 "아직 남아 소멸될 양"이 핵심이라 잔여를 보여준다 */}
                      {tab === 'EXPIRING' && row.remainPoint != null ? (
                        <BalanceText sx={{ color: '#E0483A' }}>소멸 예정 {Number(row.remainPoint).toLocaleString()} P</BalanceText>
                      ) : (
                        row.balanceAfter != null && <BalanceText>잔액 {formatBalance(row)}</BalanceText>
                      )}
                      {/* 취소는 되돌림 행이 없으므로 여기서 회수·복구액을 보여준다.
                          적립 취소=회수(잔액 감소, 빨강 -), 차감 취소=복구(잔액 증가, 청록 +) */}
                      {row.canceled && row.canceledPoint != null && (
                        <BalanceText sx={{ color: row.type === 'EARN' ? '#E0483A' : '#2E9AA4', fontWeight: 700 }}>
                          {row.type === 'EARN' ? '회수 -' : '복구 +'}
                          {Number(row.canceledPoint).toLocaleString()} P
                          {row.canceledDate ? ` · ${String(row.canceledDate).slice(0, 10)}` : ''}
                          {row.cancelStaffName ? ` · ${row.cancelStaffName}` : ''}
                        </BalanceText>
                      )}
                    </BodyCell>
                    {/* 적립분만 만료일이 있다. 규칙 없이 적립된 건은 무기한, 차감은 해당 없음 */}
                    <BodyCell sx={{ whiteSpace: 'nowrap' }}>{formatExpire(row)}</BodyCell>
                    <BodyCell>{row.staffName ?? '-'}</BodyCell>
                    <BodyCell>{getPointChannelLabel(row.channel)}</BodyCell>
                    <BodyCell>{row.description || getPointSourceLabel(row.sourceType, row.type)}</BodyCell>
                    {/* 변경 열 — 수기·일괄 내역만 수정/취소. 텍스트 버튼 두 개를 세로선으로 나눈다 */}
                    <BodyCell sx={{ whiteSpace: 'nowrap' }}>
                      <RowActions>
                        {onEditHistory && row.editable && (
                          <RowActionButton onClick={() => onEditHistory(row)}>{t('수정')}</RowActionButton>
                        )}
                        {/* 취소 가능 행(수기·일괄·구매 적립, 수기 차감). 잔여 0 적립은 비활성으로 이유를 보여준다 */}
                        {onCancelGrant && (row.cancelable || row.editable) && (
                          <RowActionButton
                            danger
                            disabled={!row.cancelable || cancelingId === row.pointHistoryId}
                            title={!row.cancelable ? '남은 포인트가 없어 취소할 수 없습니다' : undefined}
                            onClick={() => setCancelTarget(row)}>
                            {t('취소')}
                          </RowActionButton>
                        )}
                      </RowActions>
                    </BodyCell>
                  </BodyRow>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      <CommonPagination totalElements={totalElements} limit={limit} page={page} onChange={handleChangePage} />
      <ConfirmModal
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={runCancelGrant}
        message={cancelTarget?.type === 'EARN' ? '이 적립을 취소할까요?' : '이 차감을 취소할까요?'}
        highlightText="취소"
        cancelText="닫기"
        confirmText={cancelTarget?.type === 'EARN' ? '지급 취소' : '차감 취소'}
        detail={
          cancelTarget && (
            <>
              <CancelDetailBox>
                <CancelDetailRow>
                  <span>{cancelTarget.type === 'EARN' ? '적립 포인트' : '차감 포인트'}</span>
                  <b>{Number(cancelTarget.point || 0).toLocaleString()} P</b>
                </CancelDetailRow>
                {cancelTarget.type === 'EARN' && (
                  <>
                    <CancelDetailRow>
                      <span>이미 사용한 포인트</span>
                      <b>{Number(cancelUsedPoint).toLocaleString()} P</b>
                    </CancelDetailRow>
                    <CancelDetailRow>
                      <span>남은 포인트</span>
                      <b>{Number(cancelTarget.remainPoint || 0).toLocaleString()} P</b>
                    </CancelDetailRow>
                  </>
                )}
                <CancelDetailRow highlight={cancelTarget.type === 'EARN' ? 'minus' : 'plus'}>
                  <span>{cancelTarget.type === 'EARN' ? '회수할 포인트' : '다시 적립할 포인트'}</span>
                  <b>
                    {cancelTarget.type === 'EARN' ? '-' : '+'}
                    {Number((cancelTarget.type === 'EARN' ? cancelTarget.remainPoint : cancelTarget.point) || 0).toLocaleString()} P
                  </b>
                </CancelDetailRow>
                {cancelTarget.type !== 'EARN' && (
                  <CancelDetailNote>차감 취소로 적립되는 포인트의 유효기간은 수기 지급 규칙을 따릅니다.</CancelDetailNote>
                )}
              </CancelDetailBox>
              {/* 회수 불가 안내는 카드 밖, 두 문장을 줄을 나눠 표기 */}
              {cancelTarget.type === 'EARN' && (
                <CancelWarning>
                  <div>
                    {cancelUsedPoint > 0 ? (
                      <>
                        이미 사용한 <b>{Number(cancelUsedPoint).toLocaleString()}P</b>는 회수되지 않습니다.
                      </>
                    ) : (
                      '이미 사용한 포인트는 회수되지 않습니다.'
                    )}
                  </div>
                  <div>남은 포인트만 회수됩니다.</div>
                </CancelWarning>
              )}
            </>
          )
        }
      />
    </HistorySectionContainer>
  );
};

export default PointHistorySection;
